import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchCurrentSubscription,
  renewSubscription,
} from "../../../features/subscriptionSlice";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import PlansList from "../membershipPlanComponents/PlansList";
import config from "../../../config";
import PriceBreakdownCard from "../membershipPlanComponents/PriceBreakdownCard";
import OfferManager from "../../../services/home/OfferManager";
import CouponManager from "../../../services/home/CouponManager";
import EmptySubscription from "./EmptySubscription"; // adjust the path if needed
import { motion, AnimatePresence } from "framer-motion";
import Lottie from "lottie-react";
import subscriptionAnimation from "../../../loader/subscription.json";

import {
  SparklesIcon,
  CalendarIcon,
  TagIcon,
  ClockIcon,
  CurrencyRupeeIcon,
  DocumentTextIcon,
} from "@heroicons/react/24/outline";

// Subscription History View Component
const SubscriptionHistoryView = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { history, status } = useSelector((state) => state.subscription);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [offers, setOffers] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponId, setAppliedCouponId] = useState(null);

  const user = useSelector((s) => s.auth.user);
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  useEffect(() => {
    OfferManager.getActiveOffers().then((res) => {
      setOffers(res.responseData || []);
    });
  }, []);

  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return "N/A";
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    ).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getBestOfferForPackage = (pkg, subscriptionType = "RENEW") => {
    const applicableOffers = offers.filter(
      (offer) => {
        const subscriptionTypeMatches = offer.applicableSubscriptionType === "ALL" || 
                                       offer.applicableSubscriptionType === subscriptionType;
        const targetingMatches = (offer.bookPackageId === null && offer.bookPackagePlanId === null) ||
                               offer.bookPackageId === pkg.id ||
                               offer.bookPackagePlanId === pkg.planId;
        return subscriptionTypeMatches && targetingMatches;
      }
    );

    const best = applicableOffers.reduce(
      (max, curr) =>
        curr.discountPercentage > max.discountPercentage ? curr : max,
      { discountPercentage: 0 }
    );

    return best.discountPercentage;
  };

  const applyCoupon = async (code) => {
    if (!code || !selectedSubscription) {
      toast.error("Please enter a coupon code.");
      return 0;
    }

    const res = await CouponManager.validateCouponByCode({
      code,
      userId,
      planId: selectedSubscription.planId,
      packageId: selectedSubscription.packageId,
    });

    if (res.errorCode || !res.responseData?.valid) {
      toast.error(res.responseMessage || "Invalid coupon");
      setCouponDiscount(0);
      setAppliedCouponId(null);
      return 0;
    }

    const dto = res.responseData;
    const offerDisc = (selectedSubscription.selectedPackageDetails.price * getBestOfferForPackage(selectedSubscription.selectedPackageDetails, "RENEW")) / 100;
    const postOffer = selectedSubscription.selectedPackageDetails.price - offerDisc;
    const rupeeDiscount = Math.round((postOffer * dto.discountAmount) / 100);

    setCouponDiscount(rupeeDiscount);
    setAppliedCouponId(dto.couponId);
    toast.success(`Coupon applied! You saved ₹${rupeeDiscount}`);
    return rupeeDiscount;
  };

  const handleRenewWithPayment = async (couponId = null) => {
    if (!selectedSubscription) return;

    try {
      const response = await fetch(`${config.url}/api/payments/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          planId: selectedSubscription.planId,
          packageId: selectedSubscription.packageId,
          couponId,
        }),
      });

      const res = await response.json();
      
      // Check for errors in response
      if (res.responseCode !== 200 || !res.responseData) {
        toast.error(res.responseMessage || "Failed to create payment order");
        return;
      }

      const { razorpayOrderId, amount } = res.responseData;

      const options = {
        key: "rzp_live_gHTTFxw5pxLmtX",
        amount: amount * 100,
        currency: "INR",
        name: "Books at Doorstep",
        description: "Subscription Renewal",
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${config.url}/api/payments/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });

            const verifyData = await verifyRes.json();

            if (verifyData.responseCode === 200) {
              toast.success("Renewal successful!");
              dispatch(fetchCurrentSubscription(userId));
              setShowRenewModal(false);
            } else {
              toast.error("Payment verification failed!");
            }
          } catch (err) {
            console.error(err);
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: fullName,
          email: user?.emailId || "email@example.com",
          contact: user?.mobileNumber || "0000000000",
        },
        theme: {
          color: "#6366f1",
        },
      };

      const razor = new window.Razorpay(options);
      razor.open();
    } catch (error) {
      console.error(error);
      toast.error("Failed to initiate payment");
    }
  };

  const handleRenewClick = (subscription) => {
    setSelectedSubscription(subscription);
    setShowRenewModal(true);
  };

  if (status === "loading") {
    return <div className="p-8">Loading subscription history...</div>;
  }

  if (!history || history.length === 0) {
    return <EmptySubscription />;
  }

  // Sort history by creation date (latest first) and get the most recent one
  const sortedHistory = [...history].sort((a, b) => new Date(b.creationDate) - new Date(a.creationDate));
  const latestSubscription = sortedHistory[0];

  return (
    <div className="max-w-6xl mx-auto px-4 py-8">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* Left Side - Subscription History */}
        <div className="lg:col-span-2">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">Subscription History</h2>
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="divide-y divide-gray-100">
              {sortedHistory.map((sub, index) => (
                <motion.div
                  key={sub.subPlanId}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="p-4 hover:bg-gray-50 transition-colors duration-200"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-gray-900">
                          {sub.planName || "Subscription Plan"}
                        </h3>
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-medium ${
                            sub.status === "ACTIVE"
                              ? "bg-green-100 text-green-700"
                              : sub.status === "EXPIRED"
                              ? "bg-red-100 text-red-700"
                              : sub.status === "SCHEDULED"
                              ? "bg-blue-100 text-blue-700"
                              : "bg-gray-100 text-gray-700"
                          }`}
                        >
                          {sub.status}
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-4 text-sm text-gray-600">
                        <div>
                          <span className="font-medium">{sub.packageType}</span>
                          <div className="text-xs">₹{sub.selectedPackageDetails?.price || 0}</div>
                        </div>
                        <div>
                          <span className="font-medium">Start:</span>
                          <div className="text-xs">{formatDate(sub.startDate)}</div>
                        </div>
                        <div>
                          <span className="font-medium">End:</span>
                          <div className="text-xs">{formatDate(sub.endDate)}</div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Only show renew button for the latest expired subscription */}
                    {sub.status === "EXPIRED" && sub.subPlanId === latestSubscription.subPlanId && (
                      <button
                        onClick={() => handleRenewClick(sub)}
                        className="ml-4 bg-primary hover:bg-primary-shade4 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200"
                      >
                        Renew
                      </button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Right Side - Current Status */}
        <div className="lg:col-span-1">
          <div className="sticky top-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-6 text-center border border-orange-200"
            >
              <div className="relative mb-4">
                <motion.div
                  className="relative"
                  animate={{
                    scale: [1, 1.05, 1],
                  }}
                  transition={{
                    repeat: Infinity,
                    repeatType: "reverse",
                    duration: 2,
                    ease: "easeInOut"
                  }}
                >
                  <Lottie
                    animationData={subscriptionAnimation}
                    loop
                    className="w-20 h-20 mx-auto"
                  />
                  <div className="absolute inset-0 bg-primary rounded-full opacity-10 blur-lg scale-110" />
                </motion.div>
              </div>

              <h3 className="text-lg font-bold text-gray-900 mb-2">
                No Active Subscription
              </h3>
              <p className="text-gray-600 text-sm mb-4">
                Your subscription has expired. Renew or explore new plans.
              </p>

              <div className="space-y-2">
                <button
                  onClick={() => navigate("/membershipPlans")}
                  className="w-full bg-primary hover:bg-primary-shade4 text-white py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200"
                >
                  Explore New Plans
                </button>
                
                {latestSubscription && latestSubscription.status === "EXPIRED" && (
                  <button
                    onClick={() => handleRenewClick(latestSubscription)}
                    className="w-full bg-white hover:bg-gray-50 text-primary border border-primary py-2.5 px-4 rounded-lg text-sm font-medium transition-colors duration-200"
                  >
                    Renew Last Plan
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Renewal Modal */}
      {showRenewModal && selectedSubscription && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-xl">
            <h3 className="text-xl font-semibold mb-4">Renew Subscription</h3>
            <p className="text-gray-600 mb-6">
              Renewing: {selectedSubscription.planName} - {selectedSubscription.packageType}
            </p>
            
            <PriceBreakdownCard
              originalPrice={selectedSubscription.selectedPackageDetails?.price || 0}
              offerDiscount={
                (selectedSubscription.selectedPackageDetails?.price *
                  getBestOfferForPackage(selectedSubscription.selectedPackageDetails, "RENEW")) /
                100
              }
              registration={0}
              deposit={0}
              onCancel={() => {
                setShowRenewModal(false);
                setSelectedSubscription(null);
                setCouponCode("");
                setCouponDiscount(0);
                setAppliedCouponId(null);
              }}
              onApplyCoupon={applyCoupon}
              onRemoveCoupon={() => setAppliedCouponId(null)}
              onProceed={() => handleRenewWithPayment(appliedCouponId)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const SubscriptionDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const userId = useSelector((state) => state.auth.user?.userId);
  const { subscription, status, error, history: subscriptionHistory } = useSelector(
    (state) => state.subscription
  );
  const [showRenewConfirm, setShowRenewConfirm] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [showPriceBreakdown, setShowPriceBreakdown] = useState(false);
  const [offers, setOffers] = useState([]);
  const [couponCode, setCouponCode] = useState("");
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [appliedCouponId, setAppliedCouponId] = useState(null);

  const { upcomingSubscription, upcomingSubscriptionStatus } = useSelector(
    (state) => state.subscription
  );

  useEffect(() => {
    if (userId) {
      dispatch(fetchCurrentSubscription(userId));
    }
  }, [dispatch, userId]);

  const formatDate = (dateArray) => {
    if (!dateArray || !Array.isArray(dateArray)) return "N/A";
    const [year, month, day, hour, minute, second] = dateArray;
    return new Date(
      year,
      month - 1,
      day,
      hour,
      minute,
      second
    ).toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  const computeStartDateFromEnd = (endArray, remainingDays) => {
    if (!endArray || !Array.isArray(endArray) || remainingDays == null)
      return "N/A";
    const [y, m, d, hh, mm, ss] = endArray;
    // build JS Date
    const end = new Date(y, m - 1, d, hh, mm, ss);
    // subtract remainingDays
    const start = new Date(end);
    start.setDate(start.getDate() - remainingDays);
    // reuse your formatting
    return start.toLocaleDateString("en-IN", {
      weekday: "short",
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Loading, error, unsubscribed states omitted for brevity...

  // Once we have a subscription:
  const currentSubscription = subscription || {};
  const selectedPlan = currentSubscription.selectedPlan || {};
  const selectedPackage = currentSubscription.selectedPackageDetails || {};
  const startDate = computeStartDateFromEnd(
    currentSubscription.endDate,
    currentSubscription.remainingDays
  );
  const endDate = currentSubscription.endDate
    ? formatDate(currentSubscription.endDate)
    : "N/A";
  const remainingDays =
    currentSubscription.remainingDays !== undefined &&
    currentSubscription.remainingDays !== null
      ? currentSubscription.remainingDays
      : "N/A";
  const planPrice = selectedPackage.price || 0;
  const planValidityDays = selectedPlan.validityDays || 0;
  const user = useSelector((s) => s.auth.user);
  const fullName = `${user?.firstName || ""} ${user?.lastName || ""}`.trim();

  useEffect(() => {
    OfferManager.getActiveOffers().then((res) => {
      setOffers(res.responseData || []);
    });
  }, []);

  const getBestOfferForPackage = (pkg, subscriptionType = "RENEW") => {
    const applicableOffers = offers.filter(
      (offer) => {
        // Check if offer applies to this subscription type
        const subscriptionTypeMatches = offer.applicableSubscriptionType === "ALL" || 
                                       offer.applicableSubscriptionType === subscriptionType;
        
        // Check if offer applies to this package/plan
        const targetingMatches = (offer.bookPackageId === null && offer.bookPackagePlanId === null) ||
                               offer.bookPackageId === pkg.id ||
                               offer.bookPackagePlanId === pkg.planId;
        
        return subscriptionTypeMatches && targetingMatches;
      }
    );

    const best = applicableOffers.reduce(
      (max, curr) =>
        curr.discountPercentage > max.discountPercentage ? curr : max,
      { discountPercentage: 0 }
    );

    return best.discountPercentage;
  };

  const applyCoupon = async (code) => {
    if (!code) {
      toast.error("Please enter a coupon code.");
      return 0;
    }

    const res = await CouponManager.validateCouponByCode({
      code,
      userId,
      planId: selectedPlan.planId,
      packageId: selectedPackage.packageId,
    });

    // Check errorCode first
    if (res.errorCode || !res.responseData?.valid) {
      toast.error(res.responseMessage || "Invalid coupon");
      setCouponDiscount(0);
      setAppliedCouponId(null);
      return 0;
    }

    // Coupon is valid
    const dto = res.responseData;
    const pkg = selectedPlan.packages.find(
      (p) => p.packageId === selectedPackage.packageId
    );
    // For renewals, no offer discount is applied, so use original price
    const originalPrice = pkg.price;
    const rupeeDiscount = Math.round((originalPrice * dto.discountAmount) / 100);

    setCouponDiscount(rupeeDiscount);
    setAppliedCouponId(dto.couponId);
    toast.success(`Coupon applied! You saved ₹${rupeeDiscount}`);
    return rupeeDiscount;
  };

  const handleRenewWithPayment = async (couponId = null) => {
    setShowRenewConfirm(false);

    try {
      const response = await fetch(`${config.url}/api/payments/create-order`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          userId,
          planId: subscription.planId,
          packageId: subscription.packageId,
          couponId,
        }),
      });

      const res = await response.json();
      
      // Check for errors in response
      if (res.responseCode !== 200 || !res.responseData) {
        toast.error(res.responseMessage || "Failed to create payment order");
        return;
      }

      const { razorpayOrderId, amount } = res.responseData;

      const options = {
        key: "rzp_live_gHTTFxw5pxLmtX",
        amount: amount * 100,
        currency: "INR",
        name: "Books at Doorstep",
        description: "Subscription Renewal",
        order_id: razorpayOrderId,
        handler: async function (response) {
          try {
            const verifyRes = await fetch(`${config.url}/api/payments/verify`, {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              body: JSON.stringify({
                razorpayOrderId: response.razorpay_order_id,
                razorpayPaymentId: response.razorpay_payment_id,
                razorpaySignature: response.razorpay_signature,
              }),
            });

            const verifyData = await verifyRes.json();

            if (verifyData.responseCode === 200) {
              toast.success("Renewal successful!");
              dispatch(fetchCurrentSubscription(userId));
            } else {
              toast.error("Payment verification failed!");
            }
          } catch (err) {
            console.error(err);
            toast.error("Payment verification failed");
          }
        },
        prefill: {
          name: fullName,
          email: user?.emailId || "email@example.com",
          contact: user?.mobileNumber || "0000000000",
        },
        theme: {
          color: "#6366f1",
        },
      };

      const razor = new window.Razorpay(options);
      razor.open();
    } catch (error) {
      console.error(error);
      toast.error("Failed to initiate payment");
    }
  };

  return (
    <>
      {!subscription && status !== "loading" ? (
        // Show SubscriptionHistoryView if user has history, otherwise EmptySubscription
        subscriptionHistory && subscriptionHistory.length > 0 ? (
          <SubscriptionHistoryView />
        ) : (
          <EmptySubscription />
        )
      ) : (
        <>
          <div className="max-w-4xl mx-auto px-2 py-4 sm:px-4 lg:px-8">
            {/* ...rest of your subscription details UI... */}
            <div className="bg-primary-shade1/50 backdrop-blur-lg rounded-3xl p-4 sm:p-6 shadow-2xl ring-1 ring-primary-shade2/20">
              <h2 className="text-xl sm:text-2xl font-bold text-center text-primary mb-4">
                <span className="bg-clip-text text-transparent bg-gradient-to-r from-primary-shade5 to-primary">
                  Subscription Details
                </span>
              </h2>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-6">
                <DetailItem
                  icon={TagIcon}
                  label="Plan Name"
                  value={currentSubscription.planName}
                />
                <DetailItem
                  icon={DocumentTextIcon}
                  label="Package Type"
                  value={currentSubscription.packageType}
                />
                <DetailItem
                  icon={CurrencyRupeeIcon}
                  label="Price"
                  value={`₹${planPrice}`}
                />
                <DetailItem
                  icon={ClockIcon}
                  label="Validity"
                  value={`${planValidityDays} Days`}
                />
                <DetailItem
                  icon={CalendarIcon}
                  label="Start Date"
                  value={startDate}
                />
                <DetailItem
                  icon={CalendarIcon}
                  label="End Date"
                  value={endDate}
                />
                <DetailItem
                  icon={ClockIcon}
                  label="Remaining Days"
                  value={
                    remainingDays === 0 ? (
                      <span className="text-red-600 font-semibold">
                        Your subscription is expiring today. Please upgrade or
                        renew.
                      </span>
                    ) : remainingDays === "N/A" ? (
                      "N/A"
                    ) : (
                      `${remainingDays} Days`
                    )
                  }
                />
              </div>

              <div className="flex flex-wrap justify-center gap-2 sm:gap-3 mt-4">
                <ActionButton
                  onClick={() => {
                    // if an upcoming plan already exists, show a toast:
                    if (upcomingSubscription) {
                      toast.error(
                        "You already have an upcoming plan scheduled."
                      );
                      return;
                    }
                    // otherwise allow them to pick a new plan:
                    setShowUpgrade(true);
                  }}
                  text="Upgrade"
                  className="bg-primary-shade4 hover:bg-primary-shade5 text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3"
                />
                <ActionButton
                  onClick={() => {
                    if (upcomingSubscription) {
                      toast.error(
                        "You already have an upcoming plan scheduled."
                      );
                      return;
                    }
                    setShowPriceBreakdown(true);
                  }}
                  text="Renew"
                  className="bg-primary hover:bg-primary-shade3 text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3"
                />
                <ActionButton
                  onClick={() => navigate(`/upcoming-plans/${userId}`)}
                  text="Upcoming"
                  className="bg-primary-shade2 hover:bg-primary text-sm sm:text-base px-4 py-2 sm:px-6 sm:py-3"
                />
              </div>
            </div>
          </div>

          {showUpgrade && (
            <div className="mt-8 sm:mt-12">
              <h3 className="text-xl sm:text-2xl font-bold text-center text-primary mb-4 sm:mb-6">
                Select a New Plan
              </h3>
              <PlansList subscriptionType="CHANGE_PLAN" excludePlanId={currentSubscription.planId} />
            </div>
          )}

          {/* Confirmation Modal */}
          {showPriceBreakdown && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl p-4 sm:p-6 max-w-md w-full shadow-xl">
                <PriceBreakdownCard
                  key={selectedPackage.packageId}
                  originalPrice={selectedPackage.price}
                  offerDiscount={
                    (selectedPackage.price *
                      getBestOfferForPackage(selectedPackage, "RENEW")) /
                    100
                  }
                  registration={0}
                  deposit={0}
                  onCancel={() => setShowPriceBreakdown(false)}
                  onApplyCoupon={applyCoupon}
                  onRemoveCoupon={() => setAppliedCouponId(null)}
                  onProceed={() => handleRenewWithPayment(appliedCouponId)}
                />
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

const DetailItem = ({ icon: Icon, label, value }) => (
  <div className="flex items-start sm:items-center gap-3 bg-white rounded-xl px-4 py-3 shadow-sm hover:shadow-md transition-all duration-200">
    <div className="p-2 bg-primary-shade1 rounded-lg">
      <Icon className="w-5 h-5 text-primary-shade5" />
    </div>
    <div>
      <div className="text-xs text-text-muted/70">{label}</div>
      <div className="text-sm font-semibold text-text-muted">
        {value || "N/A"}
      </div>
    </div>
  </div>
);

const ActionButton = ({ onClick, text, className }) => (
  <button
    onClick={onClick}
    className={`${className} flex-1 text-white py-2 px-4 rounded-lg font-semibold transition-all duration-300 hover:scale-[1.02] min-w-[160px] text-sm`}
  >
    {text}
  </button>
);

export default SubscriptionDetails;
